import { EyeFilled, EyeOff, MoreHorizontal } from '@creditornot/cb-icons';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';

import ErrorToastContent from 'common/components/ErrorToastContent';
import { updateOfferOverrides } from 'modules/shared-menu/api';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { UpdateOfferOverridesPayload } from 'modules/shared-menu/types';

import { StyledPopover, PopoverContent, Action, ToggleButton } from './styles';
import { useOfferOverridesContext } from '../../context/OfferOverridesContext';
import { useOverrideSettings } from '../../hooks/useSelectionOverrideSettings';
import { useVenueSelectionOverrides } from '../../hooks/useSelectionOverrides';
import { Offer } from '../../types';

type OverridesOfferActionsProps = {
  offer: Offer;
  selectionId: string;
};

type UpdateOfferOverridesMutationVariables = {
  selectionId: string;
  offerId: string;
  payload: UpdateOfferOverridesPayload;
};

const OverridesOfferActions = ({ offer, selectionId }: OverridesOfferActionsProps) => {
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const scope = useScopeContext();
  const { offerAttrsEnabledForOverride } = useOverrideSettings();
  const { offerOverrides } = useOfferOverridesContext();
  const { refetchSelectionOverrides } = useVenueSelectionOverrides();
  const menuPermissions = useOfferingMenuPermissionsContext();

  const canEnableAndDisableOffer = offerAttrsEnabledForOverride.includes('enabled');

  const { mutate: executeUpdateOfferOverrides } = useMutation(
    ({ selectionId, offerId, payload }: UpdateOfferOverridesMutationVariables) =>
      updateOfferOverrides(selectionId, offerId, payload),
    {
      onSuccess: () => {
        toast.success(<FormattedMessage id="toast.success" />);
        refetchSelectionOverrides();
      },
      onError: error => {
        toast.error(<ErrorToastContent error={error} />, { duration: Infinity });
      },
    },
  );

  const handleToggleEnabled = useCallback(() => {
    /**
     * TODO: treat the case of "reset" to shared menu when the BE implements it
     * Otherwise it's always gonna save an override for every toggle (either `on` or `off` but always overridden)
     */
    const overrides = {
      ...offerOverrides,
      enabled: {
        ...(offerOverrides.enabled ?? {}),
        enabled: !offer.enabled?.enabled,
      },
    };

    executeUpdateOfferOverrides({
      selectionId,
      offerId: offer.id,
      payload: { scope, ...overrides },
    });
  }, [offerOverrides, offer.enabled?.enabled, offer.id, executeUpdateOfferOverrides, selectionId, scope]);

  if (!canEnableAndDisableOffer || !menuPermissions.contentEdit) {
    return null;
  }

  return (
    <div onClick={e => e.stopPropagation()}>
      <StyledPopover
        content={
          <PopoverContent>
            {offer.enabled.enabled ? (
              <Action icon={<EyeOff />} variant="red" onClick={() => handleToggleEnabled()}>
                <FormattedMessage id="menu-item.disable" />
              </Action>
            ) : (
              <Action icon={<EyeFilled />} variant="blue" onClick={() => handleToggleEnabled()}>
                <FormattedMessage id="menu-item.enable" />
              </Action>
            )}
          </PopoverContent>
        }
        onClickOutside={handleClose}
        onClickInside={handleClose}
        show={show}
      >
        <ToggleButton
          tabIndex={-1}
          onClick={e => {
            e.preventDefault();
            setShow(!show);
          }}
        >
          <MoreHorizontal />
        </ToggleButton>
      </StyledPopover>
    </div>
  );
};

export default React.memo(OverridesOfferActions);
